import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCreateVisita, useUpdateVisita } from '@offertify/query';
import { usersAPI } from '../../utils/api';
import { INTERNAL_CATEGORIES } from './attivitaConfig';
import { clientsAPI } from '@offertify/api-client';
import type { Cliente, Visita, VisitaStato } from '@offertify/core';
import toast from 'react-hot-toast';
import { CalendarDays, CheckCircle2, Loader2, Search, X } from 'lucide-react';
import RecurrenceFields, { useRecurrence } from './RecurrenceFields';
import RecurrenceDelete from './RecurrenceDelete';
import { useActivityTypes } from '../../hooks/useActivityTypes';

interface CalendarActivityFormProps {
  initialDate: string;
  visita?: Visita;
  onClose: () => void;
  onSuccess: () => void;
}

function clientLabel(client: Cliente): string {
  return client.ragione_sociale || [client.nome, client.cognome].filter(Boolean).join(' ') || 'Cliente senza nome';
}

export default function CalendarActivityForm({
  initialDate,
  visita,
  onClose,
  onSuccess,
}: CalendarActivityFormProps) {
  const recurrence = useRecurrence(visita);
  const [internal, setInternal] = useState(!!visita?.categoria_interna);
  const [category, setCategory] = useState<keyof typeof INTERNAL_CATEGORIES>(visita?.categoria_interna || 'riunione');
  const [fromApp, setFromApp] = useState(visita?.origine === 'app');
  const [assignee, setAssignee] = useState(visita?.user_id || '');
  const { data: agents } = useQuery({ queryKey: ['users-hierarchy'], queryFn: async () => (await usersAPI.getHierarchy()).data });
  const [date, setDate] = useState(initialDate);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<Cliente | null>((visita?.cliente as Cliente) || null);
  const [type, setType] = useState(visita?.tipo || 'visita');
  const [stato, setStato] = useState<VisitaStato>(visita?.stato || 'programmata');
  const [time, setTime] = useState(visita?.ora?.slice(0, 5) || '');
  const [note, setNote] = useState(visita?.note || '');
  const [esito, setEsito] = useState(visita?.esito || '');

  const { data: types } = useActivityTypes('cliente');
  const { data: searchResults, isFetching: isSearching } = useQuery({
    queryKey: ['calendar-activity-clients', clientSearch],
    queryFn: async () => (await clientsAPI.getClients({ search: clientSearch, limit: 12 })).data.data,
    enabled: !internal && clientSearch.trim().length >= 2 && !selectedClient,
  });
  const createVisita = useCreateVisita();
  const updateVisita = useUpdateVisita();

  const selectableTypes = (types || []).filter((item) => !item.is_system);

  const selectClient = (client: Cliente) => {
    setSelectedClient(client);
    setClientSearch('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!visita && !internal && !selectedClient) {
      toast.error('Seleziona il cliente a cui associare l’attività');
      return;
    }
    try {
      const payload = {
        cliente_id: internal ? undefined : selectedClient?.id,
        categoria_interna: internal ? category : undefined,
        origine: fromApp && !internal ? 'app' as const : 'manuale' as const,
        user_id: assignee || undefined,
        data: date,
        ricorrenza: !visita && stato === 'programmata' ? recurrence.rule : undefined,
        ora: time || undefined,
        tipo: internal ? category : fromApp ? 'telefonata' : type,
        stato,
        note: note.trim() || undefined,
        esito: stato === 'completata' ? esito.trim() || undefined : undefined,
      };
      if (visita) {
        const { cliente_id, ricorrenza, ...changes } = payload;
        const update = recurrence.scope === 'serie' ? (({ stato, esito, ...fields }) => fields)(changes) : changes;
        await updateVisita.mutateAsync({ id: visita.id, data: { ...update, scope: recurrence.scope } });
      } else {
        await createVisita.mutateAsync(payload);
      }
      toast.success(stato === 'completata' ? 'Attività completata registrata' : 'Attività programmata');
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Impossibile creare l’attività');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="calendar-activity-title">
      <button type="button" className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Chiudi finestra" />
      <form onSubmit={handleSubmit} className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-5">
          <div>
            <div className="flex items-center gap-2 text-blue-600">
              <CalendarDays className="h-5 w-5" />
              <span className="text-sm font-medium">Calendario</span>
            </div>
            <h2 id="calendar-activity-title" className="mt-1 text-lg font-semibold text-gray-900">{visita ? 'Modifica attività' : 'Nuova attività'}</h2>
            <p className="mt-1 text-sm text-gray-500">La data del giorno selezionato è già impostata.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100" aria-label="Chiudi">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={!!visita} checked={internal} onChange={(e) => { setInternal(e.target.checked); setFromApp(false); }} />Attività interna senza cliente</label>
          {visita?.offerta_id && !visita.cliente_id ? <p className="text-sm text-gray-500">Attività associata a un’offerta</p> : internal ? (
            <div><label htmlFor="internal-category" className="block text-sm font-medium">Categoria interna</label>
              <select id="internal-category" value={category} onChange={(e) => setCategory(e.target.value as keyof typeof INTERNAL_CATEGORIES)} className="w-full rounded-lg border border-gray-300 p-2">
                {Object.entries(INTERNAL_CATEGORIES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          ) : <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="calendar-activity-client">Cliente *</label>
            {selectedClient ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{clientLabel(selectedClient)}</p>
                  {selectedClient.email && <p className="truncate text-xs text-gray-500">{selectedClient.email}</p>}
                </div>
                <button type="button" disabled={!!visita} onClick={() => setSelectedClient(null)} className="text-sm font-medium text-blue-700 hover:text-blue-900">Cambia</button>
              </div>
            ) : (
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input id="calendar-activity-client" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Cerca per nome, email o P. IVA..." autoComplete="off" className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
                {clientSearch.trim().length > 0 && clientSearch.trim().length < 2 && <p className="mt-1 text-xs text-gray-500">Digita almeno 2 caratteri.</p>}
                {isSearching && <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-blue-600" />}
                {searchResults && (
                  <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {searchResults.length ? searchResults.map((client) => (
                      <li key={client.id}>
                        <button type="button" onClick={() => selectClient(client)} className="w-full px-3 py-2.5 text-left hover:bg-gray-50">
                          <span className="block text-sm font-medium text-gray-900">{clientLabel(client)}</span>
                          {(client.email || client.partita_iva) && <span className="block text-xs text-gray-500">{[client.email, client.partita_iva].filter(Boolean).join(' · ')}</span>}
                        </button>
                      </li>
                    )) : <li className="px-3 py-2.5 text-sm text-gray-500">Nessun cliente trovato.</li>}
                  </ul>
                )}
              </div>
            )}
          </div>

          }
          {!internal && (!visita || !!visita.cliente_id) && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={fromApp} onChange={(e) => setFromApp(e.target.checked)} />Richiesta da app (telefonata)</label>}
          <div><label htmlFor="activity-assignee" className="block text-sm font-medium">Venditore</label>
            <select id="activity-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)} className="w-full rounded-lg border border-gray-300 p-2">
              <option value="">Me stesso</option>
              {(agents || []).filter((u) => u.is_active && u.role !== 'cliente').map((u) => <option key={u.id} value={u.id}>{u.name || `${u.nome} ${u.cognome}`}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="calendar-activity-date">Data *</label>
              <input id="calendar-activity-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="calendar-activity-time">Ora</label>
              <input id="calendar-activity-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <RecurrenceFields value={recurrence} start={date} disabled={stato !== 'programmata'} />
          {recurrence.scope === 'singola' && <div>
            <span className="mb-2 block text-sm font-medium text-gray-700">Stato *</span>
            <div className="grid grid-cols-2 gap-2">
              {(visita ? [['programmata', 'Programmata'], ['completata', 'Completata'], ['annullata', 'Annullata'], ['rinviata', 'Rinviata']] as const : [['programmata', 'Da programmare'], ['completata', 'Già completata']] as const).map(([value, label]) => (
                <button key={value} type="button" onClick={() => setStato(value)} className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${stato === value ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                  {value === 'completata' && <CheckCircle2 className="mr-1.5 inline h-4 w-4" />}{label}
                </button>
              ))}
            </div>
          </div>

          }
          {!internal && !fromApp && <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="calendar-activity-type">Tipo</label>
            <select id="calendar-activity-type" value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500">
              {selectableTypes.map((item) => <option key={item.codice} value={item.codice}>{item.label}</option>)}
              {!selectableTypes.some((item) => item.codice === type) && <option value={type}>Visita</option>}
            </select>
          </div>

          }
          {recurrence.scope === 'singola' && stato === 'completata' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="calendar-activity-result">Esito</label>
              <input id="calendar-activity-result" value={esito} onChange={(e) => setEsito(e.target.value)} placeholder="Es. appuntamento svolto, ordine confermato" className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="calendar-activity-note">Note</label>
            <textarea id="calendar-activity-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 bg-gray-50 p-5">
          {visita && <RecurrenceDelete visita={visita} onSuccess={onSuccess} />}
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Annulla</button>
          <button type="submit" disabled={createVisita.isPending || updateVisita.isPending} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {createVisita.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {visita ? 'Salva modifiche' : stato === 'completata' ? 'Registra completata' : 'Programma attività'}
          </button>
        </div>
      </form>
    </div>
  );
}
